import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Row, Col, Button, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { Search, Filter, TrendingUp, TrendingDown } from 'react-bootstrap-icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

const StockScreener = ({ profileId, token }) => {
  const [stocks, setStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlist, setSelectedWatchlist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    sector: '',
    minMarketCap: '',
    maxMarketCap: '',
    minPrice: '',
    maxPrice: '',
    country: 'US',
    isEtf: '',
    exchange: ''
  });

  const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
  const authToken = token || localStorage.getItem('token');

  useEffect(() => {
    fetchScreenerData();
    fetchWatchlists();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [stocks, filters]);

  // Initialize filteredStocks with all stocks when stocks are first loaded
  useEffect(() => {
    if (stocks.length > 0 && filteredStocks.length === 0) {
      setFilteredStocks(stocks);
    }
  }, [stocks]);

  const fetchScreenerData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE}/api/stock-market/screener`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setStocks(response.data || []);
    } catch (error) {
      console.error('Error fetching screener data:', error);
      toast.error('Failed to fetch stock screener data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...stocks];

    // If no filters are applied, show all stocks
    const hasActiveFilters = filters.search || filters.sector || filters.minMarketCap || 
                           filters.maxMarketCap || filters.minPrice || filters.maxPrice || 
                           filters.isEtf || filters.exchange;

    if (!hasActiveFilters) {
      setFilteredStocks(filtered);
      return;
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(stock => 
        stock.symbol?.toLowerCase().includes(searchTerm) ||
        stock.companyName?.toLowerCase().includes(searchTerm)
      );
    }

    // Sector filter
    if (filters.sector) {
      filtered = filtered.filter(stock => stock.sector === filters.sector);
    }

    // Market cap filters
    if (filters.minMarketCap) {
      filtered = filtered.filter(stock => stock.marketCap >= parseFloat(filters.minMarketCap) * 1000000000);
    }
    if (filters.maxMarketCap) {
      filtered = filtered.filter(stock => stock.marketCap <= parseFloat(filters.maxMarketCap) * 1000000000);
    }

    // Price filters
    if (filters.minPrice) {
      filtered = filtered.filter(stock => stock.price >= parseFloat(filters.minPrice));
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(stock => stock.price <= parseFloat(filters.maxPrice));
    }

    // Country filter
    if (filters.country) {
      filtered = filtered.filter(stock => stock.country === filters.country);
    }

    // ETF filter
    if (filters.isEtf !== '') {
      filtered = filtered.filter(stock => stock.isEtf === (filters.isEtf === 'true'));
    }

    // Exchange filter
    if (filters.exchange) {
      filtered = filtered.filter(stock => stock.exchangeShortName === filters.exchange);
    }

    setFilteredStocks(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      sector: '',
      minMarketCap: '',
      maxMarketCap: '',
      minPrice: '',
      maxPrice: '',
      country: 'US',
      isEtf: '',
      exchange: ''
    });
  };

  const fetchWatchlists = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/stock-market/profiles/${profileId}/watchlists`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setWatchlists(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedWatchlist(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching watchlists:', error);
    }
  };

  const addToWatchlist = async (stock) => {
    if (!selectedWatchlist) {
      toast.error('Please select a watchlist first');
      return;
    }

    console.log('Adding to watchlist:', { stock, selectedWatchlist, authToken: authToken ? 'present' : 'missing' });
    try {
      const params = new URLSearchParams({
        ticker_symbol: stock.symbol,
        company_name: stock.companyName || stock.symbol,
        sector: stock.sector || '',
        exchange: stock.exchangeShortName || ''
      });
      
      await axios.post(
        `${API_BASE}/api/stock-market/watchlists/${selectedWatchlist}/stocks?${params}`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success(`${stock.symbol} added to watchlist!`);
    } catch (error) {
      console.error('Watchlist error:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        toast.error(`Authentication failed: ${error.response?.data?.detail || 'Session expired'}`);
      } else {
        toast.error(`Failed to add ${stock.symbol}: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const formatMarketCap = (marketCap) => {
    if (!marketCap) return 'N/A';
    if (marketCap >= 1e12) return `$${(marketCap / 1e12).toFixed(1)}T`;
    if (marketCap >= 1e9) return `$${(marketCap / 1e9).toFixed(1)}B`;
    if (marketCap >= 1e6) return `$${(marketCap / 1e6).toFixed(1)}M`;
    return `$${marketCap.toLocaleString()}`;
  };

  const getSectors = () => {
    const sectors = [...new Set(stocks.map(stock => stock.sector).filter(Boolean))];
    return sectors.sort();
  };

  const getExchanges = () => {
    const exchanges = [...new Set(stocks.map(stock => stock.exchangeShortName).filter(Boolean))];
    return exchanges.sort();
  };

  return (
    <div>
      {/* Results */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              Stock Screening Results ({filteredStocks.length.toLocaleString()} stocks)
            </h5>
            <div className="d-flex align-items-center gap-2">
              <Form.Select
                size="sm"
                value={selectedWatchlist || ''}
                onChange={(e) => setSelectedWatchlist(e.target.value)}
                style={{ width: '200px' }}
              >
                <option value="">Select Watchlist</option>
                {watchlists.map(watchlist => (
                  <option key={watchlist.id} value={watchlist.id}>
                    {watchlist.name}
                  </option>
                ))}
              </Form.Select>
              <Button variant="outline-secondary" size="sm" onClick={clearFilters} className="me-2">
                Clear Filters
              </Button>
              <Button variant="outline-primary" size="sm" onClick={fetchScreenerData} disabled={loading}>
                {loading ? <Spinner size="sm" /> : '🔄'} Refresh
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading stock data...</p>
            </div>
          ) : (
            <Table striped hover responsive size="sm">
              <thead>
                <tr>
                  <th>
                    Symbol
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Filter..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="mt-1"
                    />
                  </th>
                  <th>Company</th>
                  <th>
                    Price
                    <Row className="mt-1 g-1">
                      <Col>
                        <Form.Control
                          size="sm"
                          type="number"
                          placeholder="Min"
                          value={filters.minPrice}
                          onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          size="sm"
                          type="number"
                          placeholder="Max"
                          value={filters.maxPrice}
                          onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                        />
                      </Col>
                    </Row>
                  </th>
                  <th>
                    Market Cap
                    <Row className="mt-1 g-1">
                      <Col>
                        <Form.Control
                          size="sm"
                          type="number"
                          placeholder="Min (B)"
                          value={filters.minMarketCap}
                          onChange={(e) => handleFilterChange('minMarketCap', e.target.value)}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          size="sm"
                          type="number"
                          placeholder="Max (B)"
                          value={filters.maxMarketCap}
                          onChange={(e) => handleFilterChange('maxMarketCap', e.target.value)}
                        />
                      </Col>
                    </Row>
                  </th>
                  <th>
                    Sector
                    <Form.Select
                      size="sm"
                      value={filters.sector}
                      onChange={(e) => handleFilterChange('sector', e.target.value)}
                      className="mt-1"
                    >
                      <option value="">All</option>
                      {getSectors().map(sector => (
                        <option key={sector} value={sector}>{sector}</option>
                      ))}
                    </Form.Select>
                  </th>
                  <th>
                    Exchange
                    <Form.Select
                      size="sm"
                      value={filters.exchange}
                      onChange={(e) => handleFilterChange('exchange', e.target.value)}
                      className="mt-1"
                    >
                      <option value="">All</option>
                      {getExchanges().map(exchange => (
                        <option key={exchange} value={exchange}>{exchange}</option>
                      ))}
                    </Form.Select>
                  </th>
                  <th>
                    Type
                    <Form.Select
                      size="sm"
                      value={filters.isEtf}
                      onChange={(e) => handleFilterChange('isEtf', e.target.value)}
                      className="mt-1"
                    >
                      <option value="">All</option>
                      <option value="false">Stock</option>
                      <option value="true">ETF</option>
                    </Form.Select>
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredStocks.slice(0, 100).map((stock) => (
                  <tr key={stock.symbol}>
                    <td>
                      <Link to={`/stock/${stock.symbol}`} target="_blank" rel="noopener noreferrer">
                        <strong>{stock.symbol}</strong>
                      </Link>
                    </td>
                    <td>
                      <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {stock.companyName}
                      </div>
                    </td>
                    <td>
                      <span className={stock.price > 100 ? 'text-success' : stock.price < 10 ? 'text-danger' : ''}>
                        ${stock.price?.toFixed(2) || 'N/A'}
                      </span>
                    </td>
                    <td>{formatMarketCap(stock.marketCap)}</td>
                    <td>
                      <Badge bg="secondary" className="text-truncate" style={{ maxWidth: '100px' }}>
                        {stock.sector || 'N/A'}
                      </Badge>
                    </td>
                    <td>
                      <small className="text-muted">{stock.exchangeShortName}</small>
                    </td>
                    <td>
                      {stock.isEtf ? (
                        <Badge bg="info">ETF</Badge>
                      ) : stock.isFund ? (
                        <Badge bg="warning">Fund</Badge>
                      ) : (
                        <Badge bg="primary">Stock</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => addToWatchlist(stock)}
                        title="Add to watchlist"
                      >
                        +
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {filteredStocks.length > 100 && (
            <div className="text-center text-muted mt-3">
              <small>Showing first 100 results. Use filters to narrow down results.</small>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default StockScreener;
